import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import StatusBadge, { URGENCY } from "@/components/StatusBadge";
import FileUploader from "@/components/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Briefcase, DollarSign, CheckCircle2, ShieldAlert, MapPin, FileText, ShieldCheck, Download } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5">
    <div className="flex items-center justify-between">
      <p className="text-sm text-slate-500 font-medium">{label}</p>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone === "amber" ? "bg-orange-100 text-sunburst" : "bg-slate-100 text-[#0B192C]"}`}><Icon className="w-5 h-5" /></div>
    </div>
    <p className="mt-2 text-3xl font-display font-black text-slate-900 font-mono">{value}</p>
  </div>
);

const DOC_TYPES = [["license", "License"], ["insurance", "Insurance"], ["w9", "W-9"], ["other", "Other"]];

export default function VendorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docForm, setDocForm] = useState({ doc_type: "license", expiry_date: "", file: null });

  const load = async () => {
    const [s, j, d] = await Promise.all([api.get("/stats"), api.get("/work-orders"), api.get("/vendor/docs")]);
    setStats(s.data); setJobs(j.data); setDocs(d.data);
  };
  useEffect(() => { load(); }, []);

  const saveDoc = async () => {
    if (!docForm.file) return toast.error("Upload a file first");
    try {
      await api.post("/vendor/docs", {
        doc_type: docForm.doc_type, file_id: docForm.file.id, storage_path: docForm.file.storage_path,
        original_filename: docForm.file.original_filename, expiry_date: docForm.expiry_date || null,
      });
      toast.success("Document submitted for review");
      setDocForm({ doc_type: "license", expiry_date: "", file: null });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <DashboardShell>
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-black text-slate-900">My Jobs</h1>
        <p className="text-slate-500 text-sm mt-1">Accept assignments, update status, and upload proof of work.</p>
      </div>

      {user && !user.vendor_approved && (
        <div data-testid="vendor-pending-banner" className="mt-5 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">
          <ShieldAlert className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">Your account is pending approval. Upload your compliance documents below — you'll receive jobs once an admin approves you.</p>
        </div>
      )}

      {stats && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Briefcase} label="Assigned Jobs" value={stats.assigned_jobs} />
          <Stat icon={CheckCircle2} label="Active" value={stats.active} tone="amber" />
          <Stat icon={CheckCircle2} label="Completed" value={stats.completed} />
          <Stat icon={DollarSign} label="Your Earnings" value={`$${stats.earnings}`} tone="amber" />
        </div>
      )}

      <Tabs defaultValue="jobs" className="mt-8">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="jobs" data-testid="vendor-tab-jobs">Jobs</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="vendor-tab-compliance">Compliance</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-4 grid gap-4">
          {jobs.map((o) => (
            <div key={o.id} data-testid={`vendor-job-card-${o.ref}`} onClick={() => navigate(`/orders/${o.id}`)}
              className="bg-white rounded-2xl border border-slate-200 p-5 cursor-pointer hover:border-sunburst hover:shadow-md transition-all">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-ocean">{o.ref}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCY[o.urgency]?.cls}`}>{URGENCY[o.urgency]?.label}</span>
                  </div>
                  <p className="font-display font-semibold text-slate-900 mt-1">{o.service_type}</p>
                  <p className="text-sm text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{o.property_address}</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-xs text-slate-400">{o.vendor_id === user?.id ? "Your payout" : "Your bid"}</p>
                    <p className="font-mono text-xl font-bold text-emerald-600">${o.vendor_id === user?.id ? (o.vendor_cost ?? "—") : (o.my_request?.total != null ? o.my_request.total.toFixed(2) : "—")}</p>
                  </div>
                  <StatusBadge status={o.status} />
                </div>
              </div>
            </div>
          ))}
          {jobs.length === 0 && <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 text-slate-400">No jobs assigned yet.</div>}
        </TabsContent>

        <TabsContent value="compliance" className="mt-4 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-semibold text-slate-900">Upload a document</h3>
            <p className="text-sm text-slate-400 mb-4">License, insurance, W-9 and other compliance files.</p>
            <div className="space-y-4">
              <div>
                <Label>Document type</Label>
                <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expiry date (optional)</Label>
                <Input type="date" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} className="mt-1.5" />
              </div>
              <FileUploader testid="compliance-doc-upload-button" label="Upload document" accept="image/*,application/pdf"
                onUploaded={(d) => setDocForm((f) => ({ ...f, file: d }))} />
              <Button data-testid="compliance-doc-save-button" onClick={saveDoc} className="w-full bg-[#0B192C] hover:bg-[#1E3E62] text-white">Submit for review</Button>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-semibold text-slate-900">Your documents</h3>
            <div className="mt-4 space-y-2">
              {docs.length === 0 && <p className="text-sm text-slate-400">No documents uploaded yet.</p>}
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-3">
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-700 capitalize">{d.doc_type}</p>
                    <p className="text-xs text-slate-400 truncate">{d.original_filename}{d.expiry_date ? ` · exp ${d.expiry_date}` : ""}</p>
                  </div>
                  {d.expiry_flag === "expired" && <span className="text-xs text-red-600 font-semibold">Expired</span>}
                  {d.expiry_flag === "expiring_soon" && <span className="text-xs text-amber-600 font-semibold">Expiring</span>}
                  <button onClick={() => downloadFile(d.storage_path, d.original_filename)} title="Download" data-testid={`vendor-download-doc-${d.id}`} className="text-slate-400 hover:text-sunburst">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {d.status === "verified"
                    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold"><ShieldCheck className="w-3.5 h-3.5" />Verified</span>
                    : <span className="text-xs text-slate-400">Pending</span>}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
